/**
 * OverlayLayer - SVG rendering layer for all overlays
 * Elvis Aloha Collection - Arena Map Editor
 * 
 * Renders saved overlays, current drawing, and interactive handles.
 */

import React, { useMemo } from 'react';
import { 
  OVERLAY_CATEGORIES, 
  STATUS_COLORS, 
  getCategoryColor 
} from '../../constants/mapEditor';

// Resize handle size in screen pixels
const HANDLE_SIZE = 8;

const OverlayLayer = ({
  overlays,
  selectedId,
  onSelect,
  transform,
  currentDrawing,
  activeTool,
  activeCategory,
  editingVertexIndex,
  onVertexMouseDown,
  onResizeMouseDown,
  isSelectMode = false
}) => {
  /**
   * Render resize handles for selected rectangles
   */
  const renderResizeHandles = (overlay) => {
    if (selectedId !== overlay.id || overlay.type !== 'rect') return null;
    if (!isSelectMode) return null;
    
    const handles = [
      { pos: 'nw', x: overlay.x, y: overlay.y },
      { pos: 'ne', x: overlay.x + overlay.width, y: overlay.y },
      { pos: 'sw', x: overlay.x, y: overlay.y + overlay.height },
      { pos: 'se', x: overlay.x + overlay.width, y: overlay.y + overlay.height },
      // Edge handles
      { pos: 'n', x: overlay.x + overlay.width / 2, y: overlay.y },
      { pos: 's', x: overlay.x + overlay.width / 2, y: overlay.y + overlay.height },
      { pos: 'e', x: overlay.x + overlay.width, y: overlay.y + overlay.height / 2 },
      { pos: 'w', x: overlay.x, y: overlay.y + overlay.height / 2 }
    ];
    
    const cursorMap = {
      nw: 'nwse-resize', ne: 'nesw-resize',
      sw: 'nesw-resize', se: 'nwse-resize',
      n: 'ns-resize', s: 'ns-resize',
      e: 'ew-resize', w: 'ew-resize'
    };
    
    return handles.map(h => (
      <rect
        key={h.pos}
        x={h.x - HANDLE_SIZE / 2 / transform.scale}
        y={h.y - HANDLE_SIZE / 2 / transform.scale}
        width={HANDLE_SIZE / transform.scale}
        height={HANDLE_SIZE / transform.scale}
        fill="white"
        stroke="#C9A962"
        strokeWidth={1 / transform.scale}
        style={{ cursor: cursorMap[h.pos], pointerEvents: 'all' }}
        onMouseDown={(e) => {
          e.stopPropagation();
          onResizeMouseDown?.(overlay.id, h.pos, e);
        }}
        data-testid={`resize-handle-${h.pos}`}
      />
    ));
  };
  
  /**
   * Render polygon vertices for editing
   */
  const renderPolygonVertices = (overlay) => {
    if (selectedId !== overlay.id || overlay.type !== 'polygon') return null;
    if (!isSelectMode) return null;
    
    return overlay.points?.map((p, i) => (
      <circle
        key={i}
        cx={p.x}
        cy={p.y}
        r={6 / transform.scale}
        fill={editingVertexIndex === i ? '#C9A962' : 'white'}
        stroke="#C9A962"
        strokeWidth={2 / transform.scale}
        style={{ cursor: 'move', pointerEvents: 'all' }}
        onMouseDown={(e) => {
          e.stopPropagation();
          onVertexMouseDown?.(overlay.id, i, e);
        }}
        data-testid={`vertex-handle-${i}`}
      />
    ));
  };
  
  /**
   * Render a single overlay
   */
  const renderOverlay = (overlay) => {
    const color = getCategoryColor(overlay.category);
    const isSelected = selectedId === overlay.id;
    const statusColor = STATUS_COLORS[overlay.publication_status] || '#888';
    const isLocked = overlay.isLocked;
    
    if (overlay.type === 'rect') {
      return (
        <g key={overlay.id} data-testid={`overlay-${overlay.id}`}>
          <rect
            x={overlay.x}
            y={overlay.y}
            width={overlay.width}
            height={overlay.height}
            fill={color + '30'}
            stroke={isSelected ? '#C9A962' : color}
            strokeWidth={(isSelected ? 3 : 2) / transform.scale}
            strokeDasharray={isSelected ? 'none' : `${5/transform.scale} ${3/transform.scale}`}
            style={{ 
              pointerEvents: 'all', 
              cursor: isSelectMode ? (isLocked ? 'not-allowed' : 'move') : 'default' 
            }}
            onClick={(e) => { e.stopPropagation(); onSelect?.(overlay); }}
          />
          {/* Name label */}
          {overlay.name && (
            <text
              x={overlay.x + overlay.width / 2}
              y={overlay.y + overlay.height / 2}
              textAnchor="middle"
              dominantBaseline="middle"
              fill={color}
              fontSize={14 / transform.scale}
              fontWeight="600"
              style={{ pointerEvents: 'none' }}
            >
              {overlay.name}
            </text>
          )}
          {/* Status indicator */}
          <circle
            cx={overlay.x + overlay.width - 10 / transform.scale}
            cy={overlay.y + 10 / transform.scale}
            r={6 / transform.scale}
            fill={statusColor}
            style={{ pointerEvents: 'none' }}
          />
          {/* Lock indicator */}
          {isLocked && (
            <text
              x={overlay.x + 10 / transform.scale}
              y={overlay.y + 14 / transform.scale}
              fontSize={10 / transform.scale}
              fill="#666"
              style={{ pointerEvents: 'none' }}
            >
              🔒
            </text>
          )}
          {renderResizeHandles(overlay)}
        </g>
      );
    }
    
    if (overlay.type === 'polygon' && overlay.points?.length > 0) {
      const pointsStr = overlay.points.map(p => `${p.x},${p.y}`).join(' ');
      return (
        <g key={overlay.id} data-testid={`overlay-${overlay.id}`}>
          <polygon
            points={pointsStr}
            fill={color + '30'}
            stroke={isSelected ? '#C9A962' : color}
            strokeWidth={(isSelected ? 3 : 2) / transform.scale}
            style={{ 
              pointerEvents: 'all', 
              cursor: isSelectMode ? (isLocked ? 'not-allowed' : 'move') : 'default' 
            }}
            onClick={(e) => { e.stopPropagation(); onSelect?.(overlay); }}
          />
          {/* Status indicator */}
          {overlay.points[0] && (
            <circle
              cx={overlay.points[0].x}
              cy={overlay.points[0].y - 15 / transform.scale}
              r={6 / transform.scale}
              fill={statusColor}
              style={{ pointerEvents: 'none' }}
            />
          )}
          {renderPolygonVertices(overlay)}
        </g>
      );
    }
    
    if (overlay.type === 'marker') {
      return (
        <g 
          key={overlay.id} 
          style={{ pointerEvents: 'all', cursor: isSelectMode ? 'pointer' : 'default' }} 
          onClick={(e) => { e.stopPropagation(); onSelect?.(overlay); }}
          data-testid={`overlay-${overlay.id}`}
        >
          <circle
            cx={overlay.x}
            cy={overlay.y}
            r={12 / transform.scale}
            fill={color}
            stroke={isSelected ? '#C9A962' : 'white'}
            strokeWidth={2 / transform.scale}
          />
          {/* Status indicator */}
          <circle
            cx={overlay.x + 10 / transform.scale}
            cy={overlay.y - 10 / transform.scale}
            r={4 / transform.scale}
            fill={statusColor}
            style={{ pointerEvents: 'none' }}
          />
        </g>
      );
    }
    
    return null;
  };
  
  /**
   * Render current drawing in progress
   */
  const renderCurrentDrawing = () => {
    if (!currentDrawing) return null;
    
    const color = getCategoryColor(activeCategory);
    
    if (currentDrawing.type === 'rect') {
      const x = Math.min(currentDrawing.startX, currentDrawing.endX || currentDrawing.startX);
      const y = Math.min(currentDrawing.startY, currentDrawing.endY || currentDrawing.startY);
      const width = Math.abs((currentDrawing.endX || currentDrawing.startX) - currentDrawing.startX);
      const height = Math.abs((currentDrawing.endY || currentDrawing.startY) - currentDrawing.startY);
      
      return (
        <rect
          x={x}
          y={y}
          width={width}
          height={height}
          fill={color + '20'}
          stroke={color}
          strokeWidth={2 / transform.scale}
          strokeDasharray={`${4/transform.scale} ${2/transform.scale}`}
        />
      );
    }
    
    if (currentDrawing.type === 'polygon' && currentDrawing.points?.length > 0) {
      return (
        <>
          <polyline
            points={currentDrawing.points.map(p => `${p.x},${p.y}`).join(' ')}
            fill="none"
            stroke={color}
            strokeWidth={2 / transform.scale}
          />
          {currentDrawing.points.map((p, i) => (
            <circle 
              key={i} 
              cx={p.x} 
              cy={p.y} 
              r={4 / transform.scale} 
              fill={color} 
            />
          ))}
        </>
      );
    }
    
    return null;
  };
  
  // Filter visible overlays
  const visibleOverlays = useMemo(() => {
    return overlays.filter(o => o.visible !== false);
  }, [overlays]);
  
  // Coordinate transform not computed yet (viewer not ready / mid chart-switch) -
  // hide instead of rendering geometry at a stale/identity transform. This check
  // must come AFTER all hooks above so it never violates the rules of hooks.
  if (!transform) return null;
  
  return (
    <svg 
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 100
      }}
      data-testid="overlay-layer"
    >
      <g transform={`translate(${transform.translateX}, ${transform.translateY}) scale(${transform.scale})`}>
        {/* Saved overlays */}
        {visibleOverlays.map(renderOverlay)}
        
        {/* Current drawing */}
        {renderCurrentDrawing()}
      </g>
    </svg>
  );
};

export default OverlayLayer;
